/**
 * Pippi's API (cowork /v1/pippi — a thin layer over the `pippi-support` agent), ported from the back office
 * (beatly-bo-app src/components/pippi/pippiApi.ts). Every route is gated server-side: Beatly staff signed in to Omni
 * with their own verified Beatly login, or the BO's own admin session; clients only in the (closed) Omni beta. The UI
 * only mirrors that (the pill shows when `me` says available). Every call says it comes from Omni (X-Pippi-App), so
 * each case records the app.
 */
import { API_BASE_URL, authHeaders, CoworkApiError } from '../omniad/api/coworkApi';

export const PIPPI_APP = 'omni';

/** One JSON (or multipart) call to cowork as the signed-in person; a non-2xx answer throws CoworkApiError (status, body). */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const multipart = init.body instanceof FormData;
  const res = await fetch(`${API_BASE_URL}${path}`, { ...init, headers: { ...authHeaders(!multipart), 'X-Pippi-App': PIPPI_APP, ...(init.headers as Record<string, string> | undefined) } });
  if (!res.ok) {
    let body: Record<string, unknown> = {};
    try { body = (await res.json()) as Record<string, unknown>; } catch { /* not JSON */ }
    throw new CoworkApiError(typeof body.error === 'string' ? body.error : `${res.status}`, res.status, body);
  }
  return (await res.json()) as T;
}

/** The `code` of a cowork refusal (conversation_closed, conversation_full, rate_limited…), whatever the error shape. */
export function errorCode(e: unknown): string | null {
  const x = e as { code?: unknown; body?: { code?: unknown }; data?: { code?: unknown } } | null;
  const code = x?.body?.code ?? x?.data?.code ?? (typeof x?.code === 'string' ? x.code : undefined);
  return typeof code === 'string' ? code : null;
}

/** What Pippi takes (cowork checks again): images, recordings, timelines, transcripts, PDFs. */
export const PIPPI_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp,video/webm,video/mp4,audio/webm,application/json,text/plain,application/pdf,.webm,.json,.txt,.pdf';
export function acceptedFile(type: string, name: string): boolean {
  const t = (type || '').toLowerCase().split(';')[0];
  if (/^image\/(png|jpe?g|gif|webp|heic|heif)$/.test(t)) return true;
  if (['video/webm', 'video/mp4', 'audio/webm', 'audio/mp4', 'application/json', 'text/plain', 'application/pdf'].includes(t)) return true;
  return !t || t === 'application/octet-stream' ? /\.(webm|json|txt|pdf|png|jpe?g)$/i.test(name) : false;
}

export type FileKind = 'file' | 'screenshot' | 'recording' | 'show-me' | 'voice' | 'timeline' | 'transcript';
export interface PippiFile { id: string; name: string; size: number; type: string; kind?: FileKind }
export interface PageInfo { path: string; label: string; campaignId?: string; campaignName?: string }
export interface WorkNote { tool: string; label: string }
export interface CaseCard {
  rowId: string; seq: number; name: string; type: string; status: string; files: number; linked: number[]; reporter: string; announced: boolean; escalated: boolean;
}
/** A case (or an urgent flag) Pippi drafted: nothing is filed until the person confirms (cowork checks this, #182). */
export interface DraftCard {
  draftId: string; kind: 'case' | 'escalation'; name: string; type?: string; priority?: string; summary: string; linked: number[];
  regressionOf?: number; category?: string; caseSeq?: number;
}
export interface RecordingSummary { seconds: number; actions: number; failedCalls: number; hasVideo: boolean; hasVoice: boolean }

interface ItemBase { id: string; at: string }
export type PippiItem =
  | (ItemBase & { kind: 'user'; text: string; files: PippiFile[]; page?: PageInfo; via?: 'typed' | 'voice' | 'show-me'; recording?: RecordingSummary })
  | (ItemBase & { kind: 'assistant'; text: string; work: WorkNote[]; card?: CaseCard; draft?: DraftCard; stop?: string })
  | (ItemBase & { kind: 'support'; text: string; author: string });

export interface ConversationView {
  id: string; title: string; status: string; caseSeq: number | null; caseRowId: string | null; draft: string; unread: number;
  files: PippiFile[]; createdAt: string; updatedAt: string;
  /** The draft waiting for the person's OK: only its card's button files. */
  pending?: { id: string; kind: 'case' | 'escalation' } | null;
  /**
   * Its case is filed (or its how-to logged): the conversation is done. Anything new goes into a new conversation;
   * only "Add more detail" / My cases write into it again (continueCase). Older servers don't send it.
   */
  closed?: boolean;
}

export interface PippiMe {
  available: boolean; code?: string; audience?: 'staff' | 'client'; name?: string; firstName?: string; email?: string;
  conversation?: ConversationView | null; unread?: number;
}

export interface PippiCase {
  rowId: string; seq: number; name: string; type: string; status: string; owner: string; pr: string; solution: string; via: 'pippi' | 'page'; files: number;
  createdAt: string; updatedAt: string; conversationId: string | null; unread: number; lastReply: { author: string; text: string; at: string } | null;
}

export interface DeviceInfo { browser?: string; os?: string; viewport?: string; language?: string; timezone?: string }
export interface RecentError { at: string; kind: string; message: string }
export interface FailedCall { at?: string; method: string; path: string; status?: number; message?: string }
export interface TurnBody {
  text: string; via?: 'typed' | 'voice' | 'show-me'; files?: string[]; page?: PageInfo; device?: DeviceInfo; errors?: RecentError[];
  recording?: RecordingSummary & { transcript?: string; timelineText?: string; calls?: FailedCall[] };
  /** The person tapped "File this case" (or "Flag it") on this draft: cowork files it before Pippi answers. */
  confirm?: string;
  /** Writing into a closed conversation on purpose ("Add more detail", My cases): cowork refuses it otherwise. */
  continueCase?: boolean;
}

export type TurnEvent =
  | { type: 'working'; tool: string; label: string }
  | { type: 'note'; work: WorkNote }
  /** The words streamed so far were planning before a tool call: show them as a progress line, not the answer. */
  | { type: 'progress'; text: string }
  | { type: 'delta'; text: string }
  | { type: 'case'; card: CaseCard }
  | { type: 'draft'; draft: DraftCard }
  | { type: 'done'; conversation: ConversationView; messages: PippiItem[] }
  | { type: 'error'; message: string; code?: string };

/** Split an SSE buffer into complete `data:` events and the unfinished rest. */
export function parseSse(buffer: string): { events: TurnEvent[]; rest: string } {
  const blocks = buffer.split('\n\n');
  const rest = blocks.pop() ?? '';
  const events: TurnEvent[] = [];
  for (const block of blocks) {
    for (const line of block.split('\n')) {
      if (!line.startsWith('data: ')) continue;
      try { events.push(JSON.parse(line.slice(6)) as TurnEvent); } catch { /* partial or keep-alive */ }
    }
  }
  return { events, rest };
}

export const pippiApi = {
  me: () => request<PippiMe>('/v1/pippi/me'),
  create: (draft = '', campaignId?: string) => request<{ conversation: ConversationView; messages: PippiItem[] }>('/v1/pippi/conversations', { method: 'POST', body: JSON.stringify({ draft, campaignId }) }),
  conversation: (id: string) => request<{ conversation: ConversationView; messages: PippiItem[]; busy: boolean }>(`/v1/pippi/conversations/${encodeURIComponent(id)}`),
  saveDraft: (id: string, draft: string) => request<{ conversation: ConversationView }>(`/v1/pippi/conversations/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ draft }) }),
  cases: () => request<{ cases: PippiCase[]; unread: number }>('/v1/pippi/cases'),
  upload: (conversationId: string, files: Array<{ file: Blob; name: string; kind: FileKind }>, continueCase = false) => {
    const form = new FormData();
    for (const f of files) { form.append('files', f.file, f.name); form.append('kind', f.kind); }
    return request<{ files: PippiFile[] }>(`/v1/pippi/files?conversationId=${encodeURIComponent(conversationId)}${continueCase ? '&continueCase=1' : ''}`, { method: 'POST', body: form });
  },
  /** ~60 s key for a transcription-only Realtime session (Lykke's ears; Pippi's own route, pilot only). */
  earsSecret: () => request<{ clientSecret: string }>('/v1/pippi/ears-secret', { method: 'POST', body: '{}' }),
  /** POST a turn and stream its events. Resolves when the stream closes. */
  async turn(id: string, body: TurnBody, onEvent: (e: TurnEvent) => void, signal?: AbortSignal): Promise<void> {
    const res = await fetch(`${API_BASE_URL}/v1/pippi/conversations/${encodeURIComponent(id)}/turn`, {
      method: 'POST',
      headers: { ...authHeaders(true), 'X-Pippi-App': PIPPI_APP },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) {
      let message = `${res.status}`;
      let data: Record<string, unknown> | undefined;
      try { data = (await res.json()) as Record<string, unknown>; if (typeof data.error === 'string') message = data.error; } catch { /* not JSON */ }
      // data.code: rate_limited | too_many_at_once | conversation_full | busy | conversation_closed
      throw new CoworkApiError(message, res.status, data ?? {});
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseSse(buffer);
      buffer = rest;
      for (const e of events) onEvent(e);
    }
  },
};
